/**
 * Renderiza HTML de template (proposta/contrato) num iframe isolado.
 *
 * Por que iframe e nao div: o design dos templates vive em <style> — como
 * fragmento (`<style>{css}</style>{html}`, modo Blocos) OU dentro do <head>
 * de um documento completo (`<!DOCTYPE html>...`, colado no modo Codigo
 * HTML). Renderizando num div, o DOMPurify removia o <style> e o documento
 * chegava "todo branco" ao cliente — e permitir <style> num div vazaria o
 * CSS do template para a pagina inteira. O iframe contem o CSS e resolve os
 * dois problemas de uma vez.
 *
 * WHOLE_DOCUMENT e obrigatorio: sem ele o DOMPurify devolve so o conteudo
 * do <body>, descartando o <style>/<link> que vivem no <head> de templates
 * salvos como documento completo (provado empiricamente — era o "tudo
 * branco" que sobrevivia a primeira correcao). Com ele, fragmento e
 * documento completo saem no MESMO shape <html><head>...</head><body>...
 * </body></html>. ADD_TAGS link mantem Google Fonts do autor.
 *
 * Seguranca (mesma defesa em profundidade SS3.A.2 do ContratoPublico):
 * o backend renderiza em Jinja2 SandboxedEnvironment com autoescape; aqui o
 * DOMPurify mantem <style>/<link> mas continua removendo <script>, event
 * handlers e embeds (verificado nas 5 configs testadas); e o sandbox do
 * iframe SEM allow-scripts e a terceira barreira — nada do template executa.
 * `allow-same-origin` existe apenas para o parent medir a altura do
 * conteudo (auto-resize).
 */
import DOMPurify from "dompurify";
import React, { useMemo, useRef } from "react";

// fullBleed: padding 0 — o documento e uma pagina web inteira e o proprio
// template decide os respiros (um padding de base viraria calha branca em
// volta de header/faixas full-width). Sem fullBleed (preview compacto,
// fragmentos soltos), 16px de respiro evitam conteudo colado na borda.
const cssBase = (fullBleed) =>
  "body{font-family:Arial,Helvetica,sans-serif;color:#1a1a1a;margin:0;" +
  `padding:${fullBleed ? "0" : "16px"};line-height:1.5}` +
  "table{border-collapse:collapse}img{max-width:100%}";

export default function HtmlTemplateFrame({
  html,
  titulo = "Documento",
  fullBleed = false,
}) {
  const frameRef = useRef(null);

  const srcDoc = useMemo(() => {
    const limpo = DOMPurify.sanitize(html || "", {
      USE_PROFILES: { html: true },
      ADD_TAGS: ["style", "link"],
      WHOLE_DOCUMENT: true,
    });
    // Saida do WHOLE_DOCUMENT: <html...><head>...</head><body>...</body>
    // </html>. Injeta a base logo apos <head> — antes do CSS do autor,
    // que portanto vence conflitos. Fallback: embrulha (html vazio etc).
    const blocoBase = `<style>${cssBase(fullBleed)}</style>`;
    const comBase = /<head[^>]*>/i.test(limpo)
      ? limpo.replace(/<head[^>]*>/i, (m) => m + blocoBase)
      : `<html><head><meta charset="utf-8">${blocoBase}</head><body>${limpo}</body></html>`;
    return `<!doctype html>${comBase}`;
  }, [html, fullBleed]);

  const ajustarAltura = () => {
    const el = frameRef.current;
    try {
      const corpo = el?.contentDocument?.body;
      // A folga compensa o padding da base; em full-bleed nao ha padding e
      // 32px virariam faixa branca morta no rodape.
      if (corpo) el.style.height = `${corpo.scrollHeight + (fullBleed ? 0 : 32)}px`;
    } catch {
      // Sem acesso ao doc (nao deve ocorrer com srcDoc): fica no minHeight.
    }
  };

  return (
    <iframe
      ref={frameRef}
      title={titulo}
      sandbox="allow-same-origin"
      srcDoc={srcDoc}
      onLoad={ajustarAltura}
      style={{
        width: "100%",
        border: 0,
        minHeight: 480,
        background: "#fff",
        display: "block",
      }}
    />
  );
}
